import React from 'react';
import { useApp } from '../store';
import { Link, useNavigate } from 'react-router-dom';
import { RequestStatus } from '../types';
import { MapPin, Clock, DollarSign, ChevronRight, Briefcase, Zap, ChevronLeft, Hammer } from 'lucide-react';

const ArtisanDashboard: React.FC = () => {
  const { requests, currentUser } = useApp();
  const navigate = useNavigate();
  
  const availableRequests = requests.filter(r => 
    r.status === RequestStatus.OPEN || r.status === RequestStatus.POSTED || r.status === RequestStatus.CLARIFYING
  );
  
  const myJobs = requests.filter(r => r.assignedArtisanId === currentUser?.id && r.status !== RequestStatus.COMPLETED);

  return (
    <div className="space-y-10 px-4 max-w-xl mx-auto reveal">
      {/* Active Work Section */}
      {myJobs.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-2">
            <h3 className="text-xl font-black text-[#2d1b0f] tracking-tight font-serif">Active Commissions</h3>
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          </div>
          <div className="grid grid-cols-1 gap-3">
            {myJobs.map(job => (
              <Link key={job.id} to={`/request/${job.id}`} className="block bg-[#2d1b0f] p-6 rounded-[2rem] shadow-2xl transition-all hover:scale-[1.02] active:scale-100 group relative overflow-hidden border border-amber-900/30">
                <div className="absolute top-0 right-0 w-32 h-32 wood-grain opacity-10 pointer-events-none"></div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-[9px] font-black uppercase tracking-widest text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20">{job.category}</span>
                  <span className="text-[9px] font-black uppercase tracking-widest text-amber-200/40">{job.status}</span>
                </div>
                <h4 className="text-amber-50 font-black text-xl mb-2 pr-6 relative font-serif italic">
                  {job.requesterName}
                  <ChevronRight size={24} className="absolute right-0 top-1/2 -translate-y-1/2 text-amber-100/20 group-hover:text-amber-100 transition-colors" />
                </h4>
                <p className="text-amber-100/60 text-xs italic line-clamp-1">"{job.text}"</p>
                <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between text-[9px] font-black uppercase tracking-widest text-amber-200/30">
                  <div className="flex items-center gap-1"><MapPin size={12} /> {job.location?.address.split(',')[0]}</div>
                  <div className="flex items-center gap-1"><Clock size={12} /> {job.urgency}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Feed Section */}
      <div className="space-y-6">
        <div className="flex justify-between items-end px-2">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/start')} 
              className="p-2 bg-white border border-stone-200 hover:bg-stone-50 rounded-xl text-stone-600 transition-colors shadow-sm"
              title="Back to Role Selection"
            >
              <ChevronLeft size={20} />
            </button>
            <div>
              <h2 className="text-3xl font-black text-[#2d1b0f] tracking-tight font-serif">The Workshop Feed</h2>
              <p className="text-stone-500 text-xs font-bold uppercase tracking-widest">New Project Postings</p>
            </div>
          </div>
          <div className="bg-amber-100 p-2 rounded-xl text-amber-700 shadow-inner">
            <Zap size={20} fill="currentColor" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          {availableRequests.length === 0 ? (
            <div className="bg-white p-16 rounded-[2.5rem] text-center border-2 border-dashed border-stone-200 shadow-sm">
              <div className="w-16 h-16 bg-stone-50 rounded-3xl flex items-center justify-center mx-auto mb-4 text-stone-300">
                <Briefcase size={32} />
              </div>
              <h3 className="text-lg font-bold text-stone-800 font-serif">The shop is quiet</h3>
              <p className="text-stone-500 text-sm font-medium leading-relaxed">We'll notify your workshop when new local projects are posted.</p>
            </div>
          ) : (
            availableRequests.map(request => (
              <Link 
                key={request.id} 
                to={`/request/${request.id}`}
                className="group bg-white p-6 rounded-[2rem] border border-stone-100 shadow-sm hover:shadow-xl transition-all block relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-24 h-24 wood-grain opacity-[0.02] pointer-events-none"></div>
                <div className="flex justify-between items-start mb-4">
                  <div className="space-y-1">
                    <span className="text-[9px] font-black uppercase tracking-[0.2em] text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-100">
                      {request.category}
                    </span>
                    <h3 className="text-lg font-black text-[#2d1b0f] pt-1 font-serif">{request.requesterName}</h3>
                  </div>
                  <div className="text-right">
                    <span className="text-[8px] font-black text-stone-400 uppercase tracking-widest block">Proximity</span>
                    <span className="text-sm font-bold text-amber-800">0.8 mi</span>
                  </div>
                </div>

                <p className="text-sm text-stone-600 line-clamp-2 mb-6 font-medium leading-relaxed italic">"{request.text}"</p>

                <div className="flex items-center justify-between border-t border-stone-50 pt-4">
                  <div className="flex items-center gap-4 text-[9px] font-black uppercase tracking-widest text-stone-400">
                    <div className="flex items-center gap-1.5"><Clock size={14} className="text-amber-600" /> {request.urgency}</div>
                    <div className="flex items-center gap-1.5"><DollarSign size={14} className="text-emerald-600" /> {request.estimatedPrice}</div>
                  </div>
                  <div className="bg-stone-50 group-hover:bg-amber-600 group-hover:text-white text-stone-400 p-2 rounded-xl transition-all shadow-sm group-hover:shadow-lg group-hover:-translate-y-1">
                    <ChevronRight size={20} />
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      </div>
      
      <div className="text-center pt-8 opacity-10">
        <Hammer className="mx-auto text-stone-900" size={24} />
      </div>
    </div>
  );
};

export default ArtisanDashboard;